<template>
  <!-- display card if one company is selected -->
  <v-card v-if="selectedCompany" flat>
    <v-card-text class="pt-6">
      <v-form ref="form" v-model="valid" :lazy-validation="true">
        <v-row>
          <v-col cols="12" md="6">
            <h5>Firma</h5>
            <v-text-field outlined data-cy="company-name" label="Företagsnamn *" v-model="selectedCompany.name" :rules="[rules.required]" />
            <v-text-field
              class="mt-3"
              outlined
              data-cy="company-organisationsnummer"
              v-mask="mask"
              label="Organisationsnummer *"
              v-model="selectedCompany.organizationalNumber"
              :rules="[rules.required, rules.integer, rules.orgnr]"
            />
            <h5 class="mt-2">Adress</h5>
            <address-box
              ref="addressbox"
              :address="useraddress"
              :hasCountry="true"
              :borderless="true"
              @changed="updateAddress"
            />
          </v-col>
          <v-col cols="12" md="6">
            <h5>Avtal</h5>
            <v-select
              label="Region *"
              outlined
              :rules="[rules.required]"
              :items="regions"
              data-cy="select-region"
              v-model="selectedCompany.regionId"
            />
            <v-select
              outlined
              class="mt-3"
              label="Avtalstyp *"
              :rules="[rules.required]"
              :items="items"
              data-cy="select-contract-type"
              :selectable="item => item.disabled"
              v-model="selectedCompany.contractTypeId"
            />
            
            <div class="d-flex flex-column" v-if="selectedCompany.previousAgreementStatus">
              <v-text-field
                outlined
                class="mt-3"
                label="Före detta avtalstyp"
                v-model="selectedCompany.previousAgreementStatus"
                readonly
                disabled
              />
  
              <v-text-field
                outlined
                class="mt-3"
                label="Datum för avtalsändring"
                v-model="companyAgreementChangeDate"
                readonly
                disabled
              />

              <v-btn class="ml-auto" @click.stop="manageAgreementChange()" :disabled="manageAgreementButtonIsDisabled || !canUpdate">
                <v-icon left>$sl-check</v-icon>
                {{ manageAgreementButtonIsDisabled ? 'Avtalsändring granskad' : 'Granska avtalsändring' }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
    <template v-if="this.showApplicationList">
      <v-card class="ml-6 py-4" width="50%" flat>
        <v-card-text v-if="applications.length > 0" class="px-4 mt-4 subtitle-1"
          >Företaget är nu godkänt, välj en anmälan för att koppla handledare.</v-card-text
        >
        <v-card-text v-else class="px-4 mt-4 subtitle-1">Företaget är nu godkänt.</v-card-text>

        <v-list class="ma-0 pa-0">
          <v-list-item two-line v-for="(item, index) in applications" :key="index" @click="goToApplication(item.id)">
            <v-list-item-icon @click.stop="deleteApplication(item.id)">
              <v-icon>$sl-delete</v-icon>
            </v-list-item-icon>

            <v-list-item-content>
              <v-list-item-title>{{ item.state }} {{ getProfessionName(item.professionId) }}</v-list-item-title>
              <v-list-item-subtitle>Skapad {{ item.createdAt | date }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-icon>
              <v-icon v-if="item.stateId < 10">$sl-arrow-right</v-icon>
            </v-list-item-icon>
          </v-list-item>
        </v-list>
      </v-card>
    </template>
    <v-card-actions class="justify-space-between">
      <v-btn v-if="selectedCompany.id" color="secondary" @click="deleteComp" :disabled="!canDelete">
        <v-icon left>$sl-delete</v-icon>Radera
      </v-btn>
      <v-btn data-cy="save-new-company" @click="saveCompany" :disabled="!canUpdate"> <v-icon>$sl-check</v-icon>Spara </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import AddressBox from '@/components/AddressBox.vue'
const SugarDate = require('sugar/date')
import API from '@/services/api.js'
import {mapGetters, mapActions} from 'vuex'
import find from 'lodash/find'
import findIndex from 'lodash/findIndex'
import {RulesMixin, CanIDo} from '@/components/mixins'
import {mask} from 'vue-the-mask'
import get from 'lodash/get'

export default {
  name: 'CompanyInformation',

  mixins: [RulesMixin, CanIDo],
  directives: {
    mask
  },
  props: {
    selectItems: {
      type: Array,
      default: []
    },
    regions: {
      type: Array,
      default: []
    }
  },
  data() {
    return {
      valid: false,
      showRefuseMessage: false,
      showApplicationList: false,
      applications: [],
      stateId: this.draftStateId,
      professions: [],
      mask: '######-####'
    }
  },

  components: {
    AddressBox
  },
  created() {
    this.getProfessions()
  },
  computed: {
    ...mapGetters('company', ['selectedCompany']),
    ...mapGetters('basic', ['signedStateId', 'basicProfessions']),
    ...mapGetters('currentuser', ['currentUserRole']),
    suggestedDistanceOrder() {
      //- TODO - grab from server
      return [
        {value: 1, text: `${this.application.profession.professionName} Grund`},
        {value: 2, text: `${this.application.profession.professionName} Yrke`}
      ]
    },

    //- Uset to get and set data from AddressBox component

    useraddress: {
      get: function () {
        return {
          address1: this.selectedCompany.postalAddress1,
          address2: this.selectedCompany.postalAddress2,
          zip: this.selectedCompany.postalZip ? this.selectedCompany.postalZip.replace(/\s/g, '') : null,
          city: this.selectedCompany.postalCity,
          country: this.selectedCompany.postalCountry
        }
      },
      // setter
      set: function (newAddress) {
        this.selectedCompany.postalAddress1 = newAddress.address1
        this.selectedCompany.postalAddress2 = newAddress.address2
        this.selectedCompany.postalZip = newAddress.zip
        this.selectedCompany.postalCity = newAddress.city
        this.selectedCompany.postalCountry = newAddress.country
      }
    },

    items() {
      if(['region'].includes(this.currentUserRole)) {
        return this.selectItems.map(i => { return { ...i, disabled: (i.value === 8) }})
      }
      return this.selectItems
    },

    companyAgreementChangeDate() {
      return SugarDate.Date(get(this, 'selectedCompany.agreementChangeDate', '')).format('{yyyy}-{MM}-{dd}').raw
    },

    manageAgreementButtonIsDisabled() {
      return !!get(this, 'selectedCompany.agreementManagedAt', null)
    }
  },

  asyncComputed: {
    canDelete(){
      return this.canIDo('company', 'delete');
    },

    canUpdate(){
      return this.canIDo('company', 'update');
    },
  },

  methods: {
    //...mapActions('company', ['setSelectedCompany']),
    ...mapActions('company', ['setSelectedCompany', 'updateSelectedCompany']),

    goToApplication(id) {
      this.$router.push('/applications/' + id)
    },

    updateAddress(newAddress) {
      this.useraddress = newAddress
    },

    //- Used to find the specific profession name from the list based on the  professionId
    getProfessionName(itemId) {
      let profession = find(this.professions, {id: itemId})
      if (profession) {
        return '(' + profession.name + ')'
      }
    },
    //- Used to get all the Profession list
    getProfessions() {
      API.listProfessions()
        .then(response => {
          this.professions = response.data
        })
        .catch(error => {
          console.log('getProfessions error ', error.response)
        })
    },
    async deleteApplication(id) {
      const res = await this.$dialog.confirm({
        text: 'Radering av anmälan går inte att ångra.',
        title: 'Vill du radera denna anmälan?',
        actions: [
          {text: 'Avbryt', key: false, icon: '$sl-cancel'},
          {text: 'Radera', key: true, icon: '$sl-delete', outlined: true}
        ]
      })

      if (res !== true) return

      API.deleteApplication(id)
        .then(response => {
          const idx = findIndex(this.applications, ['id', id])
          if (idx > -1) {
            this.applications.splice(idx, 1)
          }
        })
        .catch(error => {
          this.$events.fire(
            'showError',
            'Tyvärr gick det inte att radera anmälan. Detta ska normalt inte hända, om detta händer igen v.v. kontakta BYN:s kansli.'
          )
          console.log(error)
        })
    },

    // * delete company from db
    async deleteComp() {
      const res = await this.$dialog.confirm({
        title: 'Vill du radera detta företag?',
        text: 'Radering av företag går inte att ångra.',
        actions: [
          {text: 'Avbryt', key: false, icon: '$sl-cancel'},
          {text: 'Radera', key: true, icon: '$sl-delete', outlined: true}
        ]
      })

      if (res !== true) {
        return
      } else {
        if (!(this.selectedCompany && this.selectedCompany.id)) {
          this.$events.fire('showError', 'Inget företag är valt, testa att ladda om sidan.')
          return
        }
        API.deleteCompany(this.selectedCompany.id)
          .then(response => {
            this.$nextTick(
              () => this.$router.push({name: 'Companies'}),
              this.$events.fire('showInfo', 'Företaget raderat.')
            )
          })
          .catch(error => {
            if (error && error.response && error.response.data.name == 'SequelizeForeignKeyConstraintError') {
              this.$events.fire('showError', 'Det går inte att radera företag med anställningar.')
            } else {
              this.$events.fire('showError', 'Det går inte att radera företaget.')
            }

            console.log('There was an error:', error.response)
          })
      }
    },

    // * save company to db
    saveCompany({additionalData = {}, onSuccessText = 'Företaget sparat.', onErrorText = 'Företaget finns redan.' }) {
      //- Trigger validations and bug out on invalid
      //- Trigger validations and bug out on invalid
      if (!(this.$refs.form.validate() && this.$refs.addressbox.$refs.form.validate())) {
        this.$events.fire('showError', 'Formuläret är inte korrekt ifyllt.')
        return
      }

      /*  */
      let orgNumber = this.selectedCompany.organizationalNumber.split('-').join('')

      let data = {
        id: this.selectedCompany.id ? this.selectedCompany.id : null,
        contractTypeId: this.selectedCompany.contractTypeId,
        contracttype: this.selectedCompany.contracttype,
        isApproved: this.selectedCompany.isApproved,
        isActive: this.selectedCompany.isActive,
        name: this.selectedCompany.name,
        organizationalNumber: orgNumber,
        postalAddress1: this.selectedCompany.postalAddress1,
        postalAddress2: this.selectedCompany.postalAddress2,
        postalCity: this.selectedCompany.postalCity,
        postalCountry: this.selectedCompany.postalCountry,
        postalZip: this.selectedCompany.postalZip,
        regionId: this.selectedCompany.regionId,
        ...additionalData
      }
      API.setCompany(data)
        .then(() => {
          if (get(this, 'selectedCompany.id', null)) {
            API.getCompany(get(this, 'selectedCompany.id', null))
              .then(res => {
                // @vuese
                // * Save companies to store
                this.updateSelectedCompany(res.data)
                this.$emit('showTabsAfterCompanyIsCreated', true)
                this.$router.push({name: 'Company', params: {id: res.data.id}}).catch(() => {})
              })

              .catch(error => {
                console.log('There was an error:', error.response)
              })
            //- Update Company information in vuex store with created company
          }
          this.$events.fire('showInfo', onSuccessText)
        })
        .catch(error => {
          if (error && error.response && error.response.data && error.response.data.name) {
            this.$events.fire('showError', onErrorText)
          }

          console.log('There was an error:', error)
        })
    },

    async manageAgreementChange() {
      if ( !get(this, 'selectedCompany.id', null) )
        return this.$events.fire('showError', 'Företaget ID saknas.')

      const res = await this.$dialog.confirm({
        title: 'Vill du bekräfta att ärendet är hanterat?',
        text: 'Tänk på att när du bekräftar att du har granskat ändringen av avtalstillhörighet så försvinner ärendet för ALLA användare.',
        actions: [
          {text: 'Avbryt', key: false, icon: '$sl-cancel'},
          {text: 'Bekräfta', key: true, icon: '$sl-check', outlined: true}
        ]
      })

      if (res !== true) return
      
      const data = { agreementManagedAt: new Date() }

      return this.saveCompany({ 
        additionalData: data, 
        onSuccessText: 'Avtalet har nu blivit granskat.',
        onErrorText: 'Något har gått snett. Var god försök igen senare.'
      })
    }
  }
}
</script>

<style scoped lang="scss">
.v-list-item__icon {
  cursor: pointer;
}
</style>
